import type { RadioSnapshot } from "@agency/shared";
import { audioPrefs } from "../audio/audioPrefs.js";

export type RadioPlaybackStatus = "idle" | "loading" | "playing" | "blocked" | "error";

/** What this browser is actually doing, as opposed to the server's desired state. */
export interface RadioPlayerState {
  status: RadioPlaybackStatus;
  stationId: string | null;
  stationName: string;
  /** Playing, but the stream is rebuffering. */
  buffering: boolean;
  error: string | null;
}

export interface StationRef {
  id: string;
  name: string;
  streamUrl: string;
}

const START_TIMEOUT_MS = 15_000;
const STALL_TIMEOUT_MS = 10_000;

const MESSAGES = {
  generic: "This station can't be played in your browser.",
  format: "This stream's format isn't supported in your browser.",
  timeout: "The station isn't responding.",
} as const;

/**
 * One `HTMLAudioElement` for the lifetime of the page, driven by the
 * server's desired state through {@link sync}. Every play attempt carries a
 * token, so late events and timers from an older station can't touch a newer one.
 */
export class RadioPlayer {
  private readonly audio = new Audio();
  private readonly listeners = new Set<(state: RadioPlayerState) => void>();
  private state: RadioPlayerState = {
    status: "idle",
    stationId: null,
    stationName: "",
    buffering: false,
    error: null,
  };
  private current: StationRef | null = null;
  private attempt = 0;
  private startTimer?: ReturnType<typeof setTimeout>;
  private stallTimer?: ReturnType<typeof setTimeout>;
  /** Station whose first `playing` event should be reported to the directory. */
  private reportFor: string | null = null;
  private gain = 1;

  constructor(private readonly onListenStarted: (stationId: string) => void) {
    this.audio.preload = "none";
    this.applyVolume();
    audioPrefs.onChange(() => this.applyVolume());

    this.audio.addEventListener("playing", () => this.handlePlaying());
    this.audio.addEventListener("waiting", () => this.handleStall());
    this.audio.addEventListener("stalled", () => this.handleStall());
    this.audio.addEventListener("error", () => {
      if (
        this.hasSource() &&
        (this.state.status === "loading" || this.state.status === "playing")
      ) {
        this.fail(
          this.audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ? "format" : "generic",
        );
      }
    });
  }

  getState(): RadioPlayerState {
    return this.state;
  }

  subscribe(listener: (state: RadioPlayerState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Gesture-unlock the element (call from a click). Safari/iOS then allow
   * plays triggered later by the server.
   */
  unlock(): void {
    if (this.hasSource()) return;
    const token = ++this.attempt;
    this.audio.src = SILENT_WAV;
    this.audio.play().then(
      () => {
        if (token === this.attempt) this.release();
      },
      () => {
        if (token === this.attempt) this.release();
      },
    );
  }

  /** Follow the server's desired state; the only entry point the store uses. */
  sync(desired: RadioSnapshot): void {
    if (!desired.playing || !desired.streamUrl) {
      if (this.state.status !== "idle" || this.hasSource()) this.stop();
      return;
    }
    // Same station: leave a playing, loading, blocked or failed stream alone.
    if (this.current?.id === desired.stationId && this.state.status !== "idle") return;
    this.selectAndPlay(
      { id: desired.stationId, name: desired.stationName, streamUrl: desired.streamUrl },
      { initiatedLocally: false },
    );
  }

  selectAndPlay(station: StationRef, { initiatedLocally }: { initiatedLocally: boolean }): void {
    const token = ++this.attempt;
    this.release();
    this.current = station;
    this.reportFor = initiatedLocally ? station.id : null;
    this.set({
      status: "loading",
      stationId: station.id,
      stationName: station.name,
      buffering: false,
      error: null,
    });

    this.startTimer = setTimeout(() => {
      if (token === this.attempt && this.state.status === "loading") this.fail("timeout");
    }, START_TIMEOUT_MS);

    this.audio.src = station.streamUrl;
    this.audio.play().catch((error: unknown) => {
      if (token !== this.attempt) return;
      const name = error instanceof DOMException ? error.name : "";
      if (name === "AbortError") return; // superseded by a newer load
      if (name === "NotAllowedError") {
        this.clearTimers();
        this.set({ status: "blocked", buffering: false, error: null });
        return;
      }
      this.fail(name === "NotSupportedError" ? "format" : "generic");
    });
  }

  /** Stop and release the stream (live radio has nothing to pause). */
  stop(): void {
    this.attempt++;
    this.release();
    this.current = null;
    this.reportFor = null;
    this.set({ status: "idle", stationId: null, stationName: "", buffering: false, error: null });
  }

  /** User action: try the current station again after an error or autoplay block. */
  retry(): void {
    if (this.current) this.selectAndPlay(this.current, { initiatedLocally: false });
  }

  /** Proximity gain, already smoothed by the scene. */
  setGain(gain: number): void {
    this.gain = Math.min(1, Math.max(0, gain));
    this.applyVolume();
  }

  private applyVolume(): void {
    this.audio.volume = audioPrefs.isMuted() ? 0 : audioPrefs.radioVolume() * this.gain;
  }

  private handlePlaying(): void {
    if (this.state.status !== "loading" && this.state.status !== "playing") return;
    this.clearTimers();
    this.set({ status: "playing", buffering: false, error: null });
    if (this.reportFor && this.reportFor === this.current?.id) {
      this.onListenStarted(this.reportFor);
      this.reportFor = null;
    }
  }

  private handleStall(): void {
    if (this.state.status !== "playing" || this.stallTimer) return;
    const token = this.attempt;
    this.set({ buffering: true });
    this.stallTimer = setTimeout(() => {
      this.stallTimer = undefined;
      if (token === this.attempt && this.state.buffering) this.fail("timeout");
    }, STALL_TIMEOUT_MS);
  }

  private fail(reason: keyof typeof MESSAGES): void {
    this.attempt++;
    this.release();
    this.set({ status: "error", buffering: false, error: MESSAGES[reason] });
  }

  /** Pause, drop the source and close the connection. */
  private release(): void {
    this.clearTimers();
    this.audio.pause();
    this.audio.removeAttribute("src");
    this.audio.load();
  }

  private hasSource(): boolean {
    return this.audio.hasAttribute("src");
  }

  private clearTimers(): void {
    clearTimeout(this.startTimer);
    clearTimeout(this.stallTimer);
    this.startTimer = undefined;
    this.stallTimer = undefined;
  }

  private set(patch: Partial<RadioPlayerState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }
}

/** A few milliseconds of silence, used only to unlock the element. */
const SILENT_WAV = (() => {
  const samples = 64;
  const bytes = new Uint8Array(44 + samples);
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) bytes[offset + i] = text.charCodeAt(i);
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + samples, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, 8000, true);
  view.setUint32(28, 8000, true);
  view.setUint16(32, 1, true);
  view.setUint16(34, 8, true);
  ascii(36, "data");
  view.setUint32(40, samples, true);
  bytes.fill(128, 44); // 8-bit silence
  return `data:audio/wav;base64,${btoa(String.fromCharCode(...bytes))}`;
})();
